<script setup>
import { ref, onMounted } from 'vue';
import { useForm, router, Head, Link } from '@inertiajs/vue3';
import draggable from 'vuedraggable';
import axios from 'axios';

const props = defineProps({
    carousels: {
        type: Array,
        default: () => []
    }
});



// State
const carouselList = ref([...props.carousels]);
const isEditing = ref(false);
const editingId = ref(null);
const imagePreviewDesktop = ref(null);
const imagePreviewMobile = ref(null);
const loadingStatusId = ref(null);
const originalData = ref(null);

const form = useForm({
    carousel_title: '',
    carousel_subtitle: '',
    carousel_image_alt: '',
    carousel_image: null,
    carousel_image_mobile: null,
    carousel_button_1_text: '',
    carousel_button_1_url: '',
    carousel_button_2_text: '',
    carousel_button_2_url: '',
    carousel_active: 1
});

// Guide Modal & Error/Warning Modal Refs
let guideModal = null;
let messageModal = null;
const modalType = ref('error'); // 'error' | 'warning'
const modalMessages = ref([]);

// Methods
const openErrorModal = (msg) => {
    modalMessages.value = Array.isArray(msg) ? msg : [msg];
    modalType.value = 'error';
    if (!messageModal) {
        messageModal = new bootstrap.Modal(document.getElementById('messageModal'));
    }
    messageModal.show();
};
const openWarningModal = (msg) => {
    modalMessages.value = Array.isArray(msg) ? msg : [msg];
    modalType.value = 'warning';
    if (!messageModal) {
        messageModal = new bootstrap.Modal(document.getElementById('messageModal'));
    }
    messageModal.show();
};
const openGuideModal = () => {
    guideModal = new bootstrap.Modal(document.getElementById('carouselGuideModal'));
    guideModal.show();
};

const handleImageChange = (event, type) => {
    const file = event.target.files[0];
    if (file) {
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
            openErrorModal('Format gambar harus JPG, PNG, atau WEBP.');
            event.target.value = '';
            return;
        }
        if (file.size > 2048 * 1024) {
             openErrorModal('Ukuran gambar maksimal 2MB.');
             event.target.value = '';
             return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            if (type === 'desktop') {
                imagePreviewDesktop.value = e.target.result;
                form.carousel_image = file;
            } else {
                imagePreviewMobile.value = e.target.result;
                form.carousel_image_mobile = file;
            }
        };
        reader.readAsDataURL(file);
    }
};

const resetForm = () => {
    form.reset();
    form.clearErrors();
    isEditing.value = false;
    editingId.value = null;
    imagePreviewDesktop.value = null;
    imagePreviewMobile.value = null;
    originalData.value = null;
    const desktopInput = document.getElementById('carousel-image');
    const mobileInput = document.getElementById('carousel-image-mobile');
    if (desktopInput) desktopInput.value = '';
    if (mobileInput) mobileInput.value = '';
};

const editCarousel = (item) => {
    isEditing.value = true;
    editingId.value = item.id;
    form.carousel_title = item.carousel_title || '';
    form.carousel_subtitle = item.carousel_subtitle || '';
    form.carousel_image_alt = item.carousel_image_alt || '';
    form.carousel_button_1_text = item.carousel_button_1_text || '';
    form.carousel_button_1_url = item.carousel_button_1_url || '';
    form.carousel_button_2_text = item.carousel_button_2_text || '';
    form.carousel_button_2_url = item.carousel_button_2_url || '';
    imagePreviewDesktop.value = item.carousel_image ? `/storage/carousel/${item.carousel_image}` : null;
    imagePreviewMobile.value = item.carousel_image_mobile ? `/storage/carousel/${item.carousel_image_mobile}` : null;
    
    // Store original data for comparison
    originalData.value = {
        carousel_title: form.carousel_title,
        carousel_subtitle: form.carousel_subtitle,
        carousel_image_alt: form.carousel_image_alt,
        carousel_button_1_text: form.carousel_button_1_text,
        carousel_button_1_url: form.carousel_button_1_url,
        carousel_button_2_text: form.carousel_button_2_text,
        carousel_button_2_url: form.carousel_button_2_url
    };
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const submitForm = async () => {
    // Show spinner first (like JenisMobil)
    form.processing = true;
    
    // Small delay to show spinner effect
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Client-side validation: check required fields
    const errors = [];
    if (!form.carousel_title || form.carousel_title.trim() === '') {
        errors.push('Judul carousel wajib diisi.');
    }
    if (!form.carousel_subtitle || form.carousel_subtitle.trim() === '') {
        errors.push('Sub Judul carousel wajib diisi.');
    }
    // For new carousel, images are required
    if (!isEditing.value) {
        if (!form.carousel_image) {
            errors.push('Image Desktop wajib diupload.');
        }
        if (!form.carousel_image_mobile) {
            errors.push('Image Mobile wajib diupload.');
        }
    }
    
    if (errors.length > 0) {
        form.processing = false;
        openErrorModal(errors);
        return;
    }
    
    // Client-side validation: check for changes when editing
    if (isEditing.value && originalData.value) {
        const currentData = {
            carousel_title: form.carousel_title,
            carousel_subtitle: form.carousel_subtitle,
            carousel_image_alt: form.carousel_image_alt || '',
            carousel_button_1_text: form.carousel_button_1_text || '',
            carousel_button_1_url: form.carousel_button_1_url || '',
            carousel_button_2_text: form.carousel_button_2_text || '',
            carousel_button_2_url: form.carousel_button_2_url || ''
        };
        
        const hasTextChanges = JSON.stringify(originalData.value) !== JSON.stringify(currentData);
        const hasImageChanges = form.carousel_image || form.carousel_image_mobile;
        
        if (!hasTextChanges && !hasImageChanges) {
            form.processing = false;
            openWarningModal('Tidak ada perubahan data yang disimpan.');
            return;
        }
    }
    
    form.processing = true;
    try {
        let response;
        if (isEditing.value) {
            const formData = new FormData();
            formData.append('_method', 'PUT');
            formData.append('id', editingId.value);
            formData.append('carousel_title', form.carousel_title);
            formData.append('carousel_subtitle', form.carousel_subtitle);
            formData.append('carousel_image_alt', form.carousel_image_alt || '');
            formData.append('carousel_button_1_text', form.carousel_button_1_text || '');
            formData.append('carousel_button_1_url', form.carousel_button_1_url || '');
            formData.append('carousel_button_2_text', form.carousel_button_2_text || '');
            formData.append('carousel_button_2_url', form.carousel_button_2_url || '');
            if (form.carousel_image) formData.append('carousel_image', form.carousel_image);
            if (form.carousel_image_mobile) formData.append('carousel_image_mobile', form.carousel_image_mobile);
            
            response = await axios.post(route('admin.carousel.update'), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } else {
            const formData = new FormData();
            formData.append('carousel_title', form.carousel_title);
            formData.append('carousel_subtitle', form.carousel_subtitle);
            formData.append('carousel_image_alt', form.carousel_image_alt || '');
            formData.append('carousel_button_1_text', form.carousel_button_1_text || '');
            formData.append('carousel_button_1_url', form.carousel_button_1_url || '');
            formData.append('carousel_button_2_text', form.carousel_button_2_text || '');
            formData.append('carousel_button_2_url', form.carousel_button_2_url || '');
            if (form.carousel_image) formData.append('carousel_image', form.carousel_image);
            if (form.carousel_image_mobile) formData.append('carousel_image_mobile', form.carousel_image_mobile);
            
            response = await axios.post(route('admin.carousel.store'), formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        }
        
        if (response.data.status === 'success') {
            resetForm();
            refreshList();
        } else if (response.data.status === 'info') {
            openWarningModal(response.data.message);
        } else {
            openErrorModal(response.data.message || 'Terjadi kesalahan.');
        }
    } catch (error) {
        if (error.response && error.response.data && error.response.data.messages) {
            openErrorModal(error.response.data.messages.join(', '));
        } else if (error.response && error.response.data && error.response.data.message) {
            openErrorModal(error.response.data.message);
        } else {
            openErrorModal('Terjadi kesalahan saat menyimpan data.');
        }
    } finally {
        form.processing = false;
    }
};

const toggleStatus = async (item) => {
    loadingStatusId.value = item.id;
    try {
        const url = `/admin/carousel/${item.id}/toggle-status`;
        const response = await axios.patch(url, {
            carousel_active: item.carousel_active ? 0 : 1
        });
        if (response.data.status === 'success') {
            item.carousel_active = response.data.carousel_active;
        } else {
            openErrorModal(response.data.message || 'Gagal mengubah status.');
        }
    } catch (error) {
        console.error('Toggle status error:', error);
        if (error.response && error.response.data && error.response.data.message) {
            openErrorModal(error.response.data.message);
        } else if (error.response && error.response.status === 404) {
            openErrorModal('Route tidak ditemukan.');
        } else if (error.response && error.response.status === 419) {
            openErrorModal('Sesi telah berakhir, silakan refresh halaman.');
        } else {
            openErrorModal('Gagal mengubah status.');
        }
    } finally {
        loadingStatusId.value = null;
    }
};

const onSortEnd = async () => {
    const sortedData = carouselList.value.map((item, index) => ({
        id: item.id,
        carousel_order: index
    }));
    
    try {
        const response = await axios.patch(route('admin.carousel.order'), { sortedData });
        if (response.data.status === 'success') {
            // Silent success
        } else {
            openErrorModal(response.data.message);
        }
    } catch (error) {
        openErrorModal('Gagal menyimpan urutan.');
    }
};

const refreshList = async () => {
   try {
       const response = await axios.get(route('admin.carousel.get'));
        if (response.data.status === 'success') {
            carouselList.value = response.data.data;
        }
   } catch (e) {
       console.error(e);
   }
};

</script>

<template>
    <Head title="Carousel Slider" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-block">
                    <h4 class="page-title">Carousel Slider</h4>
                    <div class="d-inline-block align-items-center">
                        <nav>
                            <ol class="breadcrumb">
                                <li class="breadcrumb-item">
                                    <Link :href="route('dashboard.index')"><i class="mdi mdi-home-outline"></i></Link>
                                </li>
                                <li class="breadcrumb-item active" aria-current="page">Carousel</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </div>
            </div>
        
        <section class="content">
            <div class="row">
                <!-- Form Section -->
                <div class="col-12">
                     <div class="box">
                        <div class="box-header with-border">
                            <h4 class="box-title text-primary">
                                <i class="ti-image me-2"></i>{{ isEditing ? 'Edit Data' : 'Tambah Data Baru' }}
                            </h4>
                            <div class="box-controls pull-right">
                                <button type="button" @click="openGuideModal" class="btn btn-outline-info btn-sm" title="Panduan Pengisian">
                                    <i class="fa fa-question-circle me-1"></i>
                                </button>
                            </div>
                        </div>
                        <form @submit.prevent="submitForm">
                            <div class="box-body">
                                <div class="row">
                                    <!-- Column 1: Desktop Image -->
                                    <div class="col-xl-3 col-lg-4 col-md-6 mb-3">
                                        <label for="carousel-image" class="form-label d-flex justify-content-between align-items-center">
                                            <span>Image Desktop <span class="text-danger" v-if="!isEditing">*</span></span>
                                            <span class="badge bg-primary-light text-primary">16:7</span>
                                        </label>
                                        <div class="image-preview-box desktop-preview mb-2">
                                            <div class="preview-content">
                                                <img v-if="imagePreviewDesktop" :src="imagePreviewDesktop" class="preview-img">
                                                <div class="placeholder-content" v-else>
                                                    <i class="ti-image mb-2 fs-30 text-muted"></i>
                                                    <div class="text-muted small lh-1">
                                                        <div>No Image</div>
                                                        <div class="xs-text text-muted mt-1">Min: 1335x584</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <input type="file" id="carousel-image" class="form-control form-control-sm" accept="image/jpeg,image/png,image/webp" @change="(e) => handleImageChange(e, 'desktop')">
                                        <div class="text-danger small mt-1" v-if="form.errors.carousel_image">{{ form.errors.carousel_image }}</div>
                                    </div>

                                    <!-- Column 2: Mobile Image -->
                                    <div class="col-xl-3 col-lg-4 col-md-6 mb-3">
                                        <label for="carousel-image-mobile" class="form-label d-flex justify-content-between align-items-center">
                                            <span>Image Mobile <span class="text-danger" v-if="!isEditing">*</span></span>
                                            <span class="badge bg-info-light text-info">4:3</span>
                                        </label>
                                        <div class="image-preview-box mobile-preview mb-2">
                                            <div class="preview-content">
                                                <img v-if="imagePreviewMobile" :src="imagePreviewMobile" class="preview-img">
                                                <div class="placeholder-content" v-else>
                                                    <i class="ti-mobile mb-2 fs-30 text-muted"></i>
                                                    <div class="text-muted small lh-1">
                                                        <div>No Image</div>
                                                        <div class="xs-text text-muted mt-1">Min: 412x180</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <input type="file" id="carousel-image-mobile" class="form-control form-control-sm" accept="image/jpeg,image/png,image/webp" @change="(e) => handleImageChange(e, 'mobile')">
                                        <div class="text-danger small mt-1" v-if="form.errors.carousel_image_mobile">{{ form.errors.carousel_image_mobile }}</div>
                                    </div>
                                    
                                    <!-- Column 3: Text Inputs -->
                                    <div class="col-xl-6 col-lg-4 col-12">
                                        <div class="row">
                                            <div class="col-md-6">
                                                <div class="form-group mb-3">
                                                    <label for="carousel-title" class="form-label">Judul <span class="text-danger">*</span></label>
                                                    <input type="text" id="carousel-title" name="carousel_title" v-model="form.carousel_title" class="form-control" placeholder="Judul utama">
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                 <div class="form-group mb-3">
                                                    <label for="carousel-alt" class="form-label">Alt Image</label>
                                                    <input type="text" id="carousel-alt" name="carousel_image_alt" v-model="form.carousel_image_alt" class="form-control" placeholder="SEO Alt Text">
                                                </div>
                                            </div>
                                            <div class="col-12">
                                                <div class="form-group mb-3">
                                                    <label for="carousel-subtitle" class="form-label">Sub Judul <span class="text-danger">*</span></label>
                                                    <textarea id="carousel-subtitle" name="carousel_subtitle" v-model="form.carousel_subtitle" class="form-control" rows="2" placeholder="Keterangan singkat"></textarea>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div class="row">
                                            <div class="col-md-6">
                                                <div class="form-group mb-2">
                                                    <label for="carousel-btn1-text" class="form-label">Button 1</label>
                                                    <input type="text" id="carousel-btn1-text" name="carousel_button_1_text" v-model="form.carousel_button_1_text" class="form-control mb-1" placeholder="Label">
                                                    <input type="url" id="carousel-btn1-url" name="carousel_button_1_url" v-model="form.carousel_button_1_url" class="form-control" placeholder="URL">
                                                </div>
                                            </div>
                                            <div class="col-md-6">
                                                <div class="form-group mb-2">
                                                    <label for="carousel-btn2-text" class="form-label">Button 2</label>
                                                    <input type="text" id="carousel-btn2-text" name="carousel_button_2_text" v-model="form.carousel_button_2_text" class="form-control mb-1" placeholder="Label">
                                                    <input type="url" id="carousel-btn2-url" name="carousel_button_2_url" v-model="form.carousel_button_2_url" class="form-control" placeholder="URL">
                                                </div>
                                            </div>
                                        </div>

                                        <div class="text-end mt-3">
                                            <button type="button" v-if="isEditing" @click="resetForm" class="btn btn-warning me-2"><i class="ti-trash me-1"></i> Batal</button>
                                            <button type="submit" class="btn btn-primary" :disabled="form.processing">
                                                <span v-if="form.processing" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else class="ti-save-alt me-1"></i> Simpan
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <!-- List Section -->
                <div class="col-12">
                    <div v-if="carouselList.length === 0" class="alert alert-warning text-center">
                        <i class="fa fa-info-circle me-2"></i>Belum ada data carousel.
                    </div>
                    
                    <div class="box">
                        <div class="box-body p-0">
                            <div class="table-responsive">
                                <table class="table table-hover table-bordered mb-0">
                                    <thead class="bg-light">
                                        <tr>
                                            <th width="5%" class="text-center"><i class="fa fa-bars"></i></th>
                                            <th width="15%">Desktop</th>
                                            <th width="15%">Mobile</th>
                                            <th width="35%">Detail Carousel</th>
                                            <th width="15%" class="text-center">Status</th>
                                            <th width="15%" class="text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <draggable 
                                        v-model="carouselList" 
                                        tag="tbody" 
                                        item-key="id" 
                                        handle=".drag-handle"
                                        @end="onSortEnd"
                                        :animation="200"
                                    >
                                <template #item="{element}">
                                    <tr>
                                        <td class="align-middle text-center text-muted drag-handle" style="cursor: move;">
                                            <i class="fa fa-bars"></i>
                                        </td>
                                        <td class="align-middle">
                                            <img :src="element.carousel_image ? `/storage/carousel/${element.carousel_image}` : '/back/images/card/1.jpg'" class="rounded border shadow-sm" style="width: 150px; height: auto; object-fit: cover;">
                                        </td>
                                        <td class="align-middle">
                                            <img :src="element.carousel_image_mobile ? `/storage/carousel/${element.carousel_image_mobile}` : '/back/images/card/1.jpg'" class="rounded border shadow-sm" style="width: 100px; height: auto; object-fit: cover;">
                                        </td>
                                        <td class="align-middle">
                                            <h6 class="fw-bold mb-1 text-truncate" style="max-width: 250px;">{{ element.carousel_title }}</h6>
                                            <p class="small text-muted mb-2">{{ element.carousel_subtitle }}</p>
                                            <div class="d-flex gap-1">
                                                <span v-if="element.carousel_button_1_text" class="badge badge-success-light border-success">
                                                    {{ element.carousel_button_1_text }}
                                                </span>
                                                <span v-if="element.carousel_button_2_text" class="badge badge-primary-light border-primary">
                                                    {{ element.carousel_button_2_text }}
                                                </span>
                                            </div>
                                        </td>
                                        <td class="align-middle text-center">
                                            <button 
                                                @click="toggleStatus(element)" 
                                                class="btn btn-sm rounded-1 px-3" 
                                                :class="element.carousel_active ? 'btn-success' : 'btn-danger'"
                                                :disabled="loadingStatusId === element.id"
                                                title="Klik untuk mengubah status"
                                            >
                                                <span v-if="loadingStatusId === element.id" class="spinner-border spinner-border-sm me-1"></span>
                                                <i v-else-if="element.carousel_active" class="fa fa-check me-1"></i>
                                                <i v-else class="fa fa-times me-1"></i>
                                                {{ element.carousel_active ? 'Aktif' : 'Nonaktif' }}
                                            </button>
                                        </td>
                                        <td class="align-middle text-center">
                                            <button @click="editCarousel(element)" class="btn btn-sm btn-outline-primary" title="Edit">
                                                <i class="fa fa-edit me-1"></i> Edit
                                            </button>
                                        </td>
                                    </tr>
                                </template>
                                    </draggable>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Guide Modal -->
            <div class="modal fade" id="carouselGuideModal" tabindex="-1" aria-hidden="true">
                <div class="modal-dialog modal-lg">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title"><i class="fa fa-book me-2"></i>Panduan Pengisian Carousel</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div class="modal-body">
                            <div class="alert alert-info">
                                <i class="fa fa-info-circle me-2"></i>
                                <strong>Tips:</strong> Ikuti panduan ini untuk hasil yang optimal (SEO & Tampilan).
                            </div>
                            <div class="row">
                                <div class="col-md-6">
                                    <h6>📷 Ukuran Gambar</h6>
                                    <ul class="small text-muted">
                                        <li><strong>Desktop:</strong> Rasio 16:7 (1335x584 px).</li>
                                        <li><strong>Mobile:</strong> Rasio 4:3 (412x180 px).</li>
                                        <li>Format: JPG, PNG, WEBP. Max 2MB.</li>
                                    </ul>
                                </div>
                                <div class="col-md-6">
                                    <h6>📝 Konten</h6>
                                    <ul class="small text-muted">
                                        <li><strong>Judul:</strong> Singkat & Padat.</li>
                                        <li><strong>Alt:</strong> Deskripsi gambar untuk SEO.</li>
                                        <li><strong>Tombol:</strong> Kosongkan jika tidak perlu.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Tutup</button>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Error/Warning Modal -->
            <div class="modal fade" id="messageModal" tabindex="-1" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header text-white" :class="modalType === 'warning' ? 'bg-warning text-dark' : 'bg-danger'">
                            <h5 class="modal-title">
                                <i class="me-2" :class="modalType === 'warning' ? 'fa fa-exclamation-triangle' : 'ti-alert'"></i>
                                {{ modalType === 'warning' ? 'Peringatan' : 'Peringatan Validasi' }}
                            </h5>
                            <button type="button" class="btn-close" :class="modalType === 'warning' ? '' : 'btn-close-white'" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="mb-3" v-if="modalType === 'error'">Mohon perbaiki kesalahan berikut sebelum menyimpan:</p>
                            <p class="mb-3" v-else>Informasi sistem:</p>
                            <ul class="list-group list-group-flush">
                                <li class="list-group-item" :class="modalType === 'warning' ? 'list-group-item-warning' : 'list-group-item-danger'" v-for="(msg, index) in modalMessages" :key="index">
                                    <i class="fa me-2" :class="modalType === 'warning' ? 'fa-info-circle' : 'fa-exclamation-circle'"></i>{{ msg }}
                                </li>
                            </ul>
                        </div>
                        <div class="modal-footer text-end">
                            <button type="button" class="btn" :class="modalType === 'warning' ? 'btn-warning' : 'btn-danger'" data-bs-dismiss="modal"><i class="fa fa-check-square-o me-2"></i>Mengerti</button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<style scoped>
.drag-handle {
    cursor: move;
}

/* Image Preview Styles */
.image-preview-box {
    position: relative;
    width: 100%;
    border: 1px solid #e4e6ef;
    border-radius: 0.45rem;
    background-color: #f5f8fa;
    overflow: hidden;
}

.preview-content {
    position: relative;
    width: 100%;
    height: 0;
}

.desktop-preview .preview-content {
    padding-bottom: 43.75%; /* 16:7 Aspect Ratio */
}

.mobile-preview .preview-content {
    padding-bottom: 75%; /* 4:3 Aspect Ratio */
}

.preview-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.placeholder-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.fs-30 { font-size: 30px; }
</style>


